import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { AnimatePresence } from 'framer-motion';

import { AnalyticsEvents, AnalyticsProps } from '@llura/constants';

import { useCoachStore } from '../../stores/coach.store';
import { useIntroStore } from '../../stores/intro.store';
import { usePersonalityStore } from '../../stores/personality.store';
import { useHaptic } from '../../hooks/useHaptic';
import { usePushSubscription } from '../../hooks/usePushSubscription';
import { useScrollToTop } from '../../hooks/use-scroll-to-top';
import { trackEvent } from '../../lib/analytics';
import { IntroWelcome } from './screens/intro-1-welcome';
import { IntroPrinciple } from './screens/intro-2-principle';
import { IntroPhilosophy } from './screens/intro-3-philosophy';
import { IntroRitual, type RitualOutcome } from './screens/intro-4-ritual';
import { IntroPact, type SafeWordSource } from './screens/intro-5-pact';

type Step = 1 | 2 | 3 | 4 | 5;

/**
 * Linear 5-screen story intro. State machine lives entirely in this component
 * — the five screens are pure render layers that bubble back/next.
 *
 * `/` redirects here for fresh users; configured users are auto-routed to
 * `/dashboard` by the root redirect, so this page is only ever seen pre-coach.
 *
 * Analytics: fires `intro_started` once on first mount (skipping React
 * StrictMode's double-effect in dev) and `intro_completed` with the
 * safe-word source ("chip" | "custom" | "none") and ritual cadence on the
 * final action. Per-screen time-on-page comes from each screen's
 * `useStepTiming` call.
 *
 * Notification permission: requested only when the user actively commits to a
 * cadence on screen 4. Skipping the ritual screen leaves the prompt for later.
 */
export function IntroFlowPage() {
  const navigate = useNavigate();
  const haptic = useHaptic();
  const { subscribe } = usePushSubscription();
  const setIntroCompleted = useIntroStore((s) => s.setCompleted);
  const isConfigured = useCoachStore((s) => s.isConfigured);
  const personalitySeen = usePersonalityStore((s) => s.seen);
  const startedAtRef = useRef<number>(performance.now());
  const startedRef = useRef(false);
  const ritualOutcomeRef = useRef<RitualOutcome>({ kind: 'skip' });

  const [step, setStep] = useState<Step>(1);

  useScrollToTop(step);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    startedAtRef.current = performance.now();
    trackEvent(AnalyticsEvents.IntroStarted, {});
  }, []);

  const next = () => {
    haptic.tap();
    if (step < 4) {
      setStep(((step + 1) as Step));
    }
  };

  const back = () => {
    haptic.tap();
    if (step > 1) setStep(((step - 1) as Step));
  };

  const goToLogin = () => {
    haptic.tap();
    navigate('/login');
  };

  const advanceToPact = (outcome: RitualOutcome) => {
    haptic.tap();
    ritualOutcomeRef.current = outcome;
    if (outcome.kind === 'set') {
      // Permission ask is the payoff for the user's commitment. Silent denial.
      subscribe().catch(() => {
        /* user denied — silent */
      });
    }
    setStep(5);
  };

  const completeFromPact = (source: SafeWordSource) => {
    haptic.success();
    const outcome = ritualOutcomeRef.current;
    trackEvent(AnalyticsEvents.IntroCompleted, {
      [AnalyticsProps.SafeWordSource]: source,
      [AnalyticsProps.DurationMs]: Math.round(performance.now() - startedAtRef.current),
      [AnalyticsProps.RitualSkipped]: outcome.kind === 'skip',
      [AnalyticsProps.RitualCount]: outcome.kind === 'set' ? outcome.count : null,
      [AnalyticsProps.RitualPeriod]: outcome.kind === 'set' ? outcome.period : null,
      [AnalyticsProps.RitualIntensity]: outcome.kind === 'set' ? outcome.intensity : null,
    });
    setIntroCompleted(true);
    // Configured users (re-running intro) skip the test. Fresh users see it
    // once unless they've already explicitly skipped on a prior session.
    const dest = isConfigured
      ? '/dashboard'
      : personalitySeen
        ? '/onboarding/coach'
        : '/personality-test';
    navigate(dest, { replace: true });
  };

  return (
    <AnimatePresence mode="wait">
      {step === 1 && <IntroWelcome key="1" onNext={next} onLogin={goToLogin} />}
      {step === 2 && <IntroPrinciple key="2" onBack={back} onNext={next} onLogin={goToLogin} />}
      {step === 3 && (
        <IntroPhilosophy key="3" onBack={back} onNext={next} onLogin={goToLogin} />
      )}
      {step === 4 && (
        <IntroRitual key="4" onBack={back} onComplete={advanceToPact} onLogin={goToLogin} />
      )}
      {step === 5 && (
        <IntroPact key="5" onBack={back} onComplete={completeFromPact} onLogin={goToLogin} />
      )}
    </AnimatePresence>
  );
}
